import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import LinearProgress from "@mui/material/LinearProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { store } from "../../redux/store";
import {
  GetQuizResultByDateCall,
  GetQuizTitlesCall,
} from "../../redux/api/quizTitle.api";
import {
  listAssignedQuizzesCall,
  startAttemptCall,
  getAttemptCall,
  submitAttemptCall,
  getAttemptResultCall,
  listMyAttemptsCall,
} from "../../redux/api/quizSystem.api";

const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

export default function StudentQuizzes() {
  const navigate = useNavigate();
  const lang = store.getState().general?.language || "en";
  const [assigned, setAssigned] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [quizTitles, setQuizTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentAttempt, setCurrentAttempt] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [result, setResult] = useState(null);
  const [resultDialog, setResultDialog] = useState(false);
  const [historyDialog, setHistoryDialog] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [quizHistory, setQuizHistory] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const answersRef = React.useRef({});
  answersRef.current = answers;

  const loadAssigned = async () => {
    const res = await listAssignedQuizzesCall();
    setAssigned(Array.isArray(res?.data) ? res.data : []);
  };

  const loadAttempts = async () => {
    const res = await listMyAttemptsCall();
    setAttempts(Array.isArray(res?.data) ? res.data : []);
  };

  const loadQuizTitles = async () => {
    const res = await GetQuizTitlesCall({ lang });
    const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
    setQuizTitles(list);
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([loadAssigned(), loadAttempts(), loadQuizTitles()]).finally(() =>
      setLoading(false)
    );
  }, []);

  const startQuiz = async (quizAssignmentId) => {
    setLoading(true);
    const res = await startAttemptCall(quizAssignmentId);
    const data = res?.data;
    if (data?.id) {
      setCurrentAttempt(data);
      setCurrentQuestionIndex(0);
      setAnswers({});
      setTimeLeft((data.time_limit_minutes || 30) * 60);
      setResult(null);
    }
    setLoading(false);
  };

  const getInProgressAttempt = (quizId) =>
    attempts.find((a) => a.quiz_assignment_id === quizId && a.status === "in_progress");

  const submitCurrentAttempt = React.useCallback(async () => {
    if (!currentAttempt?.id || submitting) return;
    setSubmitting(true);
    const answerList = Object.entries(answersRef.current).map(([question_bank_id, selected_answer_key]) => ({
      question_bank_id,
      selected_answer_key,
    }));
    const res = await submitAttemptCall(currentAttempt.id, answerList);
    const data = res?.data;
    setResult(data);
    setResultDialog(true);
    setCurrentAttempt(null);
    setSubmitting(false);
    loadAttempts();
  }, [currentAttempt?.id, submitting]);

  useEffect(() => {
    if (!currentAttempt || timeLeft === null || timeLeft <= 0) return;
    const t = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(t);
          setSubmitting(true);
          const answerList = Object.entries(answersRef.current).map(([question_bank_id, selected_answer_key]) => ({
            question_bank_id,
            selected_answer_key,
          }));
          submitAttemptCall(currentAttempt.id, answerList).then((res) => {
            setResult(res?.data);
            setResultDialog(true);
            setCurrentAttempt(null);
            setSubmitting(false);
            loadAttempts();
          });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [currentAttempt?.id, timeLeft]);

  const handleSelectAnswer = (questionBankId, key) => {
    setAnswers((prev) => ({ ...prev, [questionBankId]: key }));
  };

  const handleSubmit = async () => {
    await submitCurrentAttempt();
  };

  const viewResult = async (attemptId) => {
    const res = await getAttemptResultCall(attemptId);
    setResult(res?.data);
    setResultDialog(true);
  };

  const resumeAttempt = async (attemptId) => {
    const res = await getAttemptCall(attemptId);
    const data = res?.data;
    if (data?.id) {
      setCurrentAttempt(data);
      setCurrentQuestionIndex(0);
      const started = new Date(data.started_at).getTime();
      const limitSec = (data.time_limit_minutes || 30) * 60;
      const elapsed = Math.floor((Date.now() - started) / 1000);
      setTimeLeft(Math.max(0, limitSec - elapsed));
      setAnswers({});
    }
  };

  const loadLegacyQuizHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await GetQuizResultByDateCall();
      const grouped = res?.data && typeof res.data === "object" ? res.data : {};
      const flat = Object.entries(grouped).flatMap(([date, list]) => {
        const arr = Array.isArray(list) ? list : [];
        return arr.map((it) => {
          const row = it?.reservation != null ? it.reservation : it;
          return { ...row, date };
        });
      });
      flat.sort((a, b) => {
        const tA = new Date(a?.created_at || a?.date || 0).getTime();
        const tB = new Date(b?.created_at || b?.date || 0).getTime();
        return tB - tA;
      });
      setQuizHistory(flat);
    } finally {
      setHistoryLoading(false);
    }
  };

  if (currentAttempt) {
    const questions = currentAttempt.questions || [];
    const total = questions.length;
    const current = questions[currentQuestionIndex];
    const progress = total ? ((currentQuestionIndex + 1) / total) * 100 : 0;

    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>
          <Card sx={{ p: 2 }}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <MDTypography variant="h6">{currentAttempt.quiz_title || "Quiz"}</MDTypography>
              <MDTypography variant="button" color={timeLeft <= 60 ? "error" : "text"}>
                Time left: {formatTime(timeLeft)}
              </MDTypography>
            </MDBox>
            <LinearProgress value={progress} color="info" sx={{ mb: 2 }} />
            <MDTypography variant="caption">Question {currentQuestionIndex + 1} of {total}</MDTypography>

            {current && (
              <MDBox mt={2}>
                <MDTypography variant="body1" fontWeight="medium" sx={{ mb: 2 }}>
                  {current.question_content}
                </MDTypography>
                <FormControl component="fieldset">
                  <RadioGroup
                    value={answers[current.question_bank_id] || ""}
                    onChange={(e) => handleSelectAnswer(current.question_bank_id, e.target.value)}
                  >
                    {(current.answers || []).map((a) => (
                      <FormControlLabel
                        key={a.key}
                        value={a.key}
                        control={<Radio />}
                        label={a.text}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </MDBox>
            )}

            <MDBox mt={3} display="flex" justifyContent="space-between">
              <Button
                disabled={currentQuestionIndex === 0}
                onClick={() => setCurrentQuestionIndex((i) => i - 1)}
              >
                Previous
              </Button>
              {currentQuestionIndex < total - 1 ? (
                <Button variant="contained" onClick={() => setCurrentQuestionIndex((i) => i + 1)}>
                  Next
                </Button>
              ) : (
                <MDButton variant="gradient" color="info" onClick={handleSubmit} disabled={submitting}>
                  {submitting ? "Submitting..." : "Submit Quiz"}
                </MDButton>
              )}
            </MDBox>
          </Card>
        </MDBox>

        <Dialog open={resultDialog} onClose={() => setResultDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { bgcolor: "background.card", color: "text.main" } }}>
          <DialogTitle>Quiz Result</DialogTitle>
          <DialogContent>
            {result && (
              <MDBox>
                <MDTypography>Score: {result.score}%</MDTypography>
                <MDTypography>Correct: {result.correct_count} / {result.total_questions}</MDTypography>
              </MDBox>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResultDialog(false)}>Close</Button>
            {result?.id && (
              <Button onClick={() => { setResultDialog(false); viewResult(result.id); }}>
                View details
              </Button>
            )}
          </DialogActions>
        </Dialog>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
          mb={3}
        >
          <MDTypography variant="h4" fontWeight="medium">
            My Quizzes
          </MDTypography>
          <MDButton
            variant="outlined"
            color="info"
            size="small"
            onClick={async () => {
              setHistoryDialog(true);
              await loadLegacyQuizHistory();
            }}
          >
            Quiz History
          </MDButton>
        </MDBox>

        {loading ? (
          <MDTypography>Loading...</MDTypography>
        ) : (
          <MDBox>
            <MDTypography variant="h6" mb={1.5}>
              General Quizzes
            </MDTypography>
            {quizTitles.length === 0 ? (
              <MDTypography color="text" sx={{ mb: 3 }}>
                No quiz titles available yet.
              </MDTypography>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(3, minmax(0, 1fr))",
                    lg: "repeat(5, minmax(0, 1fr))",
                  },
                  gap: 1.25,
                  mb: 3,
                }}
              >
                {quizTitles.map((q) => (
                  <Card
                    key={q.id}
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      border: "1px solid rgba(148,163,184,0.28)",
                      background:
                        "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.96) 100%)",
                      transition: "all .24s ease",
                      "&:hover": {
                        transform: "translateY(-3px)",
                        boxShadow: "0 14px 28px rgba(2, 6, 23, 0.1)",
                        borderColor: "rgba(59,130,246,0.35)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        aspectRatio: "16 / 9",
                        minHeight: 92,
                        borderRadius: 1.4,
                        overflow: "hidden",
                        bgcolor: "rgba(15,23,42,0.04)",
                        border: "1px solid rgba(148,163,184,0.24)",
                        mb: 1.1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {q.image ? (
                        <Box
                          component="img"
                          src={q.image}
                          alt={q.title || "Quiz"}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            objectPosition: "center",
                            backgroundColor: "#f8fafc",
                          }}
                        />
                      ) : (
                        <MDTypography variant="caption" color="text">
                          No image
                        </MDTypography>
                      )}
                    </Box>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        minHeight: 34,
                        mb: 0.8,
                        fontSize: "0.78rem",
                        lineHeight: 1.25,
                      }}
                    >
                      {q.title || "Quiz"}
                    </MDTypography>
                    <MDButton
                      variant="gradient"
                      color="info"
                      size="small"
                      fullWidth
                      sx={{
                        borderRadius: 1,
                        minHeight: 30,
                        fontSize: "0.62rem",
                        boxShadow: "0 4px 10px rgba(37,99,235,0.22)",
                      }}
                      onClick={() => navigate(`/showQuestions/${q.id}/${lang}`)}
                    >
                      Start Quiz
                    </MDButton>
                  </Card>
                ))}
              </Box>
            )}

            {assigned.length === 0 ? (
              <MDTypography color="text">No quizzes assigned yet.</MDTypography>
            ) : (
              assigned.map((q) => {
                const inProgress = getInProgressAttempt(q.id);
                return (
                  <Card key={q.id} sx={{ p: 2, mb: 2 }}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                      <MDBox>
                        <MDTypography variant="h6">{q.title}</MDTypography>
                        <MDTypography variant="caption">Time limit: {q.time_limit_minutes} min</MDTypography>
                      </MDBox>
                      <MDBox>
                        {inProgress ? (
                          <MDButton
                            variant="gradient"
                            color="info"
                            onClick={() => resumeAttempt(inProgress.id)}
                          >
                            Continue Quiz
                          </MDButton>
                        ) : (
                          <MDButton
                            variant="gradient"
                            color="info"
                            onClick={() => startQuiz(q.id)}
                          >
                            Start Quiz
                          </MDButton>
                        )}
                      </MDBox>
                    </MDBox>
                  </Card>
                );
              })
            )}
          </MDBox>
        )}

        {attempts.filter((a) => a.status === "submitted").length > 0 && (
          <MDBox mt={4}>
            <MDTypography variant="h6" mb={2}>Past attempts</MDTypography>
            {attempts.filter((a) => a.status === "submitted").slice(0, 10).map((a) => (
              <Card key={a.id} sx={{ p: 2, mb: 1 }}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                  <MDTypography>{a.quizAssignment?.title || "Quiz"} — Score: {a.score}%</MDTypography>
                  <Button size="small" onClick={() => viewResult(a.id)}>View result</Button>
                </MDBox>
              </Card>
            ))}
          </MDBox>
        )}
      </MDBox>
      <Dialog
        open={historyDialog}
        onClose={() => setHistoryDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: "background.card", color: "text.main" } }}
      >
        <DialogTitle>Quiz History</DialogTitle>
        <DialogContent>
          {historyLoading ? (
            <MDTypography variant="body2" color="text">
              Loading...
            </MDTypography>
          ) : quizHistory.length === 0 ? (
            <MDTypography variant="body2" color="text">
              No quiz history yet.
            </MDTypography>
          ) : (
            quizHistory.slice(0, 20).map((a, idx) => (
                <Card key={a?.resultId || a?.id || idx} sx={{ p: 1.5, mb: 1.2 }}>
                  <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                    <MDTypography variant="button">
                      {a?.title || "Quiz"}
                    </MDTypography>
                    <MDTypography variant="caption" fontWeight="bold">
                      {a?.score ?? 0}
                    </MDTypography>
                  </MDBox>
                  <MDTypography variant="caption" color="text">
                    {a?.created_at || a?.date
                      ? new Date(a.created_at || a.date).toLocaleString()
                      : ""}
                  </MDTypography>
                </Card>
              ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}
